<script setup lang="ts">
/*
 * FILE: unified_ui/src/components/CompletionToast.vue
 * PURPOSE: Floating in-app notification banner for search completion.
 *          Appears at the top when a completion message arrives via
 *          pollInbox, auto-dismisses after 6s. Glass design.
 */
import { watch, ref, onUnmounted } from 'vue'
import { useChatStore } from '@/stores/chat'

const chat = useChatStore()

const visible = ref(false)
const text = ref('')
let dismissTimer: ReturnType<typeof setTimeout> | null = null

function clearTimer() {
  if (dismissTimer !== null) {
    clearTimeout(dismissTimer)
    dismissTimer = null
  }
}

function dismiss() {
  visible.value = false
  clearTimer()
  chat.dismissNotification()
}

watch(
  () => chat.notification,
  (n) => {
    clearTimer()
    if (n) {
      text.value = n.text
      visible.value = true
      dismissTimer = setTimeout(dismiss, 6000)
    } else {
      visible.value = false
    }
  },
)

onUnmounted(() => clearTimer())
</script>

<template>
  <Transition name="toast">
    <div
      v-if="visible"
      class="fixed inset-x-0 top-0 z-50 pointer-events-none pt-safe"
    >
      <div class="pointer-events-auto mx-auto max-w-md px-4 pt-4">
        <div class="card-heavy flex items-start gap-3 shadow-glass-lg animate-toast-in">
          <!-- check icon -->
          <svg class="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <p class="flex-1 text-sm font-medium text-ink-800 dark:text-slate-100 leading-snug">{{ text }}</p>
          <button
            type="button"
            class="shrink-0 -mr-0.5 -mt-0.5 rounded-full p-1 text-ink-400 hover:text-ink-600 dark:text-slate-500 dark:hover:text-slate-300"
            @click="dismiss"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.animate-toast-in {
  animation: toast-slide-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.toast-enter-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
